import type { UserManager } from "oidc-client-ts";
import { openBankIdApp } from "../hooks/login/open-bankid";
import type { OidcUser, OidcUserState } from "../hooks/login/user-manager";
import { sleep } from "../utils";
import {
	authStatusQuery,
	bankIdTokenQuery,
	cancelAuthQuery,
	refreshAuthQuery,
	verifyAuthQuery,
} from "./endpoints/login";
import { sveaFetch, unwrapJsonResponse } from "./fetch";
import type { BankIdMode } from "./types";

export type StartAuthResponse = { qrCode: string | null; bankIdToken: string };

export type AuthStatusResponse = {
	qrCode: string | null;
	status: "successful" | "pending";
};

type UserInfo = {
	refreshToken: string;
	bearerToken: string;
};

export const performLogin = (
	baseUrl: string,
	appVersion: string,
	mode: BankIdMode,
	setQrCode: (qrCode: string | null) => void,
	setBankIdToken: (token: string) => void,
	maxTime: number,
	isSameDevice: boolean,
	ssn?: string | null,
): {
	extendTimeout: () => void;
	getTimeRemaining: () => number;
	tokens: Promise<{
		bearerToken: string;
		refreshToken: string;
	}>;
} => {
	let startTime = Date.now();

	const extendTimeout = () => {
		startTime = Date.now();
	};

	const getTimeRemaining = () => maxTime - (Date.now() - startTime);

	const startSign = async () => {
		const bankIdTokenreq = bankIdTokenQuery(mode, "SveaBank-e5RdD5", ssn);

		const bankIdResponse = (await sveaFetch(
			baseUrl,

			bankIdTokenreq.url,
			appVersion,
			{
				method: bankIdTokenreq.method,
				body: JSON.stringify(bankIdTokenreq.body),
			},
		).then(unwrapJsonResponse)) as StartAuthResponse;

		if (isSameDevice) openBankIdApp(bankIdResponse.bankIdToken);

		return bankIdResponse;
	};

	const signStatus = async (token: string) => {
		const authStatusReq = authStatusQuery(token);

		return (await unwrapJsonResponse(
			await sveaFetch(baseUrl, authStatusReq.url, appVersion, {
				method: authStatusReq.method,
				body: JSON.stringify(authStatusReq.body),
			}),
		)) as AuthStatusResponse;
	};

	const verifySign = async (token: string) => {
		const verifyAuthReq = verifyAuthQuery(token);
		return (await unwrapJsonResponse(
			await sveaFetch(baseUrl, verifyAuthReq.url, appVersion, {
				method: verifyAuthReq.method,
				body: JSON.stringify(verifyAuthReq.body),
			}),
		)) as UserInfo;
	};

	const cancelSign = async (token: string) => {
		const cancelLoginReq = cancelAuthQuery(token);

		await sveaFetch(baseUrl, cancelLoginReq.url, appVersion, {
			method: cancelLoginReq.method,
			body: JSON.stringify(cancelLoginReq.body),
		});
	};

	const startOrder = async () => {
		let keepCalling = true;
		let status: "successful" | "pending" = "pending";

		setTimeout(() => {
			keepCalling = false;
		}, 29000);

		while (keepCalling) {
			const { bankIdToken: startSignBankIdToken } = await startSign();
			setBankIdToken(startSignBankIdToken);

			while (keepCalling) {
				const statusResponse = await signStatus(startSignBankIdToken);
				setQrCode(statusResponse.qrCode);
				status = statusResponse.status;

				if (Date.now() - startTime >= maxTime) throw new Error("BankIdTimeout");

				if (status === "successful") break;
				await sleep(1000);
			}
			if (status === "successful") return startSignBankIdToken;

			await cancelSign(startSignBankIdToken);
		}

		if (Date.now() - startTime < maxTime) return await startOrder();

		// If maxTime is surpassed, throw error
		throw new Error("BankIdTimeout");
	};

	const getTokens = async () => {
		const token = await startOrder();
		const verifyResult = await verifySign(token);

		return {
			bearerToken: verifyResult.bearerToken,
			refreshToken: verifyResult.refreshToken,
		};
	};

	return {
		extendTimeout,
		getTimeRemaining,
		tokens: getTokens(),
	};
};

export const performSSOLogin = async (
	userManager: UserManager,
	urlParams: URLSearchParams,
	state?: OidcUserState,
	acr_values?: string,
	max_age?: string,
	scope?: string,
) => {
	const user = (await userManager.getUser()) as OidcUser | null;
	if (!user) {
		if (urlParams.get("code")) {
			const signedInUser = await userManager.signinRedirectCallback();
			const {
				refresh_token: refreshToken,
				access_token: bearerToken,
				profile: { ssn, sub },
				state: userState,
			} = signedInUser as OidcUser;
			return { bearerToken, refreshToken, ssn: ssn || sub, userState };
		} else {
			await userManager.signinRedirect({
				acr_values: acr_values,
				max_age: max_age ? +max_age : undefined,
				state: state,
				scope,
			});
		}
	} else {
		const {
			refresh_token: refreshToken,
			access_token: bearerToken,
			profile: { ssn, sub },
			state: userState,
		} = user as OidcUser;

		return { bearerToken, refreshToken, ssn: ssn || sub, userState };
	}
};

export const cancelLogin = (baseUrl: string, appVersion: string, token?: string) => {
	const cancelLoginReq = cancelAuthQuery(token || null);

	return sveaFetch(baseUrl, cancelLoginReq.url, appVersion, {
		method: cancelLoginReq.method,
		body: JSON.stringify(cancelLoginReq.body),
	});
};

export const refreshToken = async (
	baseUrl: string,
	appVersion: string,
	refreshToken: string,
): Promise<{
	bearerToken: string;
	refreshToken: string;
}> => {
	const refreshAuthReq = refreshAuthQuery(refreshToken);

	const response = await sveaFetch(baseUrl, refreshAuthReq.url, appVersion, {
		method: refreshAuthReq.method,
		body: JSON.stringify(refreshAuthReq.body),
	}).then(unwrapJsonResponse);

	return Promise.resolve({
		bearerToken: response.bearerToken,
		refreshToken: response.refreshToken,
	});
};
